import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import type { CuentaPorPagarConProveedor } from '../../types/domain';

const COP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

/** Cuentas por pagar (DAF v2, sección 19): compras a crédito con saldo
 * pendiente, pagos parciales vía el RPC `pagar_proveedor`
 * (supabase/schema_compras.sql) — nunca se edita `saldo_pendiente`
 * directamente, siempre queda el rastro en `pagos_proveedores`. */
export function CuentasPorPagar() {
  const [error, setError] = useState<string | null>(null);

  const {
    data,
    loading,
    refetch,
  } = useSupabaseQuery<CuentaPorPagarConProveedor[]>(async () => {
    const { data, error } = await supabase
      .from('cuentas_por_pagar')
      .select('*, compras ( proveedores ( nombre ) )')
      .neq('estado', 'pagada')
      .order('created_at', { ascending: false });

    if (error) return { data: null, error };

    const items = (data as unknown as Array<Record<string, unknown>>).map((row) => {
      const compra = row.compras as { proveedores: { nombre: string } | null } | null;
      return {
        ...(row as unknown as CuentaPorPagarConProveedor),
        proveedor_nombre: compra?.proveedores?.nombre ?? '—',
      };
    });

    return { data: items, error: null };
  });

  function alPagar() {
    setError(null);
    refetch();
  }

  return (
    <div>
      <PageHeader title="Cuentas por pagar" description="Compras a crédito con saldo pendiente — DAF v2, sección 19" />

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}

      {data && data.length === 0 && <EmptyState title="No hay cuentas por pagar pendientes" />}

      {data && data.length > 0 && (
        <div className="space-y-3">
          {data.map((cxp) => (
            <FilaCuenta key={cxp.id} cxp={cxp} onPagado={alPagar} onError={setError} />
          ))}
        </div>
      )}
    </div>
  );
}

function FilaCuenta({
  cxp,
  onPagado,
  onError,
}: {
  cxp: CuentaPorPagarConProveedor;
  onPagado: () => void;
  onError: (msg: string | null) => void;
}) {
  const [mostrar, setMostrar] = useState(false);
  const [monto, setMonto] = useState(String(cxp.saldo_pendiente));
  const [guardando, setGuardando] = useState(false);

  async function abonar() {
    onError(null);
    setGuardando(true);
    const { error } = await supabase.rpc('pagar_proveedor', {
      p_cuenta_por_pagar_id: cxp.id,
      p_monto: Number(monto) || 0,
      p_forma_pago_id: null,
    });
    setGuardando(false);

    if (error) {
      onError(error.message);
      return;
    }

    setMostrar(false);
    onPagado();
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-onyx">{cxp.proveedor_nombre}</p>
          <p className="text-xs text-onyx/50">
            Total {COP.format(cxp.monto_total)} · Saldo pendiente{' '}
            <span className="font-medium text-onyx/80">{COP.format(cxp.saldo_pendiente)}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`badge ${cxp.estado === 'parcial' ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-700'}`}>
            {cxp.estado}
          </span>
          <button type="button" className="btn-secondary" onClick={() => setMostrar((v) => !v)}>
            {mostrar ? 'Cancelar' : 'Abonar'}
          </button>
        </div>
      </div>

      {mostrar && (
        <div className="mt-3 flex items-center gap-3 border-t border-onyx/10 pt-3">
          <input
            type="number"
            min={0}
            max={cxp.saldo_pendiente}
            step="0.01"
            className="input-field max-w-xs"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
          />
          <button type="button" disabled={guardando} className="btn-primary" onClick={abonar}>
            {guardando ? 'Abonando…' : 'Confirmar abono'}
          </button>
        </div>
      )}
    </div>
  );
}
